import s from "./ScrollingSwapPromo.module.css"
import { useContext, useEffect, useRef, useState } from "react"
import { usePostHog } from "posthog-js/react"
import { Trans, useTranslation } from "react-i18next"
import { useFirestoreDataUpdate } from "../../hooks/useFirestoreDataUpdate.js"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"
import outfitBefore from "../../assets/scrolling-swap/outfit-before.mp4"
import outfitBeforePoster from "../../assets/scrolling-swap/outfit-before.jpg"
import outfitAfter from "../../assets/scrolling-swap/outfit-after.mp4"
import outfitAfterPoster from "../../assets/scrolling-swap/outfit-after.jpg"
import productBefore from "../../assets/scrolling-swap/product-before.mp4"
import productBeforePoster from "../../assets/scrolling-swap/product-before.jpg"
import productAfter from "../../assets/scrolling-swap/product-after.mp4"
import productAfterPoster from "../../assets/scrolling-swap/product-after.jpg"
import bothBefore from "../../assets/scrolling-swap/both-before.mp4"
import bothBeforePoster from "../../assets/scrolling-swap/both-before.jpg"
import bothAfter from "../../assets/scrolling-swap/both-after.mp4"
import bothAfterPoster from "../../assets/scrolling-swap/both-after.jpg"
import wordmark from "../../assets/scrolling-swap/wordmark-white.svg"

// Cross-promo for Swap, the scrolling.ai engine that remakes a video ad with
// another person, product or outfit. A panel slides in from the left, in the
// middle of the screen (at the bottom on phones), with little text, one promise ("copy any video in
// 1 click") and a looping demo: the original plays, a cursor clicks Copy, a
// lime scan turns it into the copy, the other swaps fan out behind it, and the
// next loop features the next swap. Closed, it folds into the scrolling.ai
// :scrolling-scroll: emoji, which brings the panel back. No Kopy-only deal:
// it points at scrolling.ai's own plans (from $19). Everything runs on CSS keyframes,
// in scrolling.ai's look (graphite, Acid Lime, Figtree) so the visitor lands on
// the same look they clicked.
//
// The clips are real Swap results from scrolling.ai, cut down to the size they
// show at (360 px wide). Each original and its copy are frame for frame the
// same length, so the scan line meets the same moment on both sides.
const SITE = "https://scrolling.ai"

// Master switch for the promo; set to false to hide it from every store.
export const SCROLLING_SWAP_PROMO_ENABLED = true

// The swaps the demo takes turns on, one per loop. `kinds` lights up the
// matching chips while that swap is on.
const SLIDES = [
	// The "Change the person" shoe clip, with the shoes swapped instead: white
	// shoes become red sneakers in her hands.
	{
		kinds: ["product"],
		before: { video: productBefore, poster: productBeforePoster },
		after: { video: productAfter, poster: productAfterPoster },
	},
	// The Swap page's "Change the outfit": a charcoal set becomes a pink one.
	{
		kinds: ["outfit"],
		before: { video: outfitBefore, poster: outfitBeforePoster },
		after: { video: outfitAfter, poster: outfitAfterPoster },
	},
	// "Change both": another person at the mic, with a can in his hand.
	{
		kinds: ["person", "product"],
		before: { video: bothBefore, poster: bothBeforePoster },
		after: { video: bothAfter, poster: bothAfterPoster },
	},
]

// How long the home page shows before the panel slides in.
const ARRIVE_MS = 1200

// scrolling.ai's middleware stores utm_* as the visitor's first and last touch,
// so signups from Kopy show up as utm_source=kopy in its admin. utm_content
// tells apart the button and the demo.
const UTM = "utm_source=kopy&utm_medium=referral&utm_campaign=swap_promo"
const swapUrl = (content) => `${SITE}/?${UTM}&utm_content=${content}`

// No Kopy-only deal: the panel points at scrolling.ai's own plans, from Starter.
const FROM_PRICE = "$5"

const CHIPS = [
	{ key: "person", icon: UserIcon },
	{ key: "product", icon: BagIcon },
	{ key: "outfit", icon: ShirtIcon },
]

export const ScrollingSwapPromo = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const updateFirestoreUserData = useFirestoreDataUpdate()
	const posthog = usePostHog()
	const { t } = useTranslation()

	const panelRef = useRef(null)
	const lead = useRef(null)
	const follow = useRef(null)
	const behind = useRef([])
	const seen = useRef(false)
	// Nothing shows until the page has settled; then the panel, or the icon
	// for someone who closed it on an earlier visit.
	const [arrived, setArrived] = useState(false)
	const [open, setOpen] = useState(false)
	const [inView, setInView] = useState(false)
	const [entered, setEntered] = useState(false)
	const [at, setAt] = useState(0)
	const reduced = useReducedMotion()

	const slide = SLIDES[at]
	// The other two, fanned out behind the phone: the next one on the left.
	const others = [1, 2].map((k) => SLIDES[(at + k) % SLIDES.length])

	useEffect(() => {
		const id = setTimeout(() => {
			setArrived(true)
			setOpen(!state.hasClosedScrollingSwapPromo)
		}, ARRIVE_MS)
		return () => clearTimeout(id)
		// Decided once per visit, from what was saved before it.
	}, [])

	// Animate only while the panel is on screen; count one view per visit.
	useEffect(() => {
		const el = panelRef.current
		if (!el || !window.IntersectionObserver) return
		const io = new IntersectionObserver(
			([entry]) => {
				setInView(entry.isIntersecting)
				if (entry.isIntersecting) setEntered(true)
				if (entry.isIntersecting && !seen.current) {
					seen.current = true
					posthog?.capture("scrolling_swap_promo_viewed")
				}
			},
			{ threshold: 0.35 },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [posthog])

	// The copy leads and the original follows its clock, so the scan always
	// shows the same moment on both sides. The two swaps behind play along.
	// A new swap loads new clips, so they start again.
	useEffect(() => {
		const a = lead.current
		const b = follow.current
		if (!a || !b) return
		const all = [a, b, ...behind.current.filter(Boolean)]
		if (open && inView && !reduced) {
			b.currentTime = a.currentTime
			all.forEach((v) => v.play().catch(() => {}))
		} else {
			all.forEach((v) => v.pause())
		}
	}, [at, open, inView, reduced])

	const clicked = (target) => () =>
		posthog?.capture("scrolling_swap_promo_clicked", { target })

	const save = (fields) => {
		dispatch(fields)
		updateFirestoreUserData(fields)
	}

	// Closing keeps the icon; the next visit starts with the icon too.
	const close = () => {
		posthog?.capture("scrolling_swap_promo_closed")
		setOpen(false)
		save({ hasClosedScrollingSwapPromo: true })
	}

	const reopen = () => {
		posthog?.capture("scrolling_swap_promo_reopened")
		setOpen(true)
		save({ hasClosedScrollingSwapPromo: false })
	}

	return (
		<div className={s.dock}>
			<section
				ref={panelRef}
				className={s.panel}
				data-open={open || undefined}
				data-entered={entered || undefined}
				data-paused={!(open && inView) || undefined}
				aria-label={t("components.scrolling_swap_promo.aria_label")}
				aria-hidden={!open || undefined}
			>
				<div aria-hidden="true" className={s.glow} />
				<div aria-hidden="true" className={s.dots} />

				<button
					type="button"
					className={s.close}
					onClick={close}
					aria-label={t("components.scrolling_swap_promo.close")}
					title={t("components.scrolling_swap_promo.close")}
				>
					<XIcon />
				</button>

				<div className={s.grid}>
					<a
						href={swapUrl("demo")}
						target="_blank"
						rel="noopener noreferrer"
						className={s.stage}
						onClick={clicked("demo")}
						aria-label={t(
							"components.scrolling_swap_promo.demo_label",
						)}
					>
						{others.map((other, i) => (
							<video
								key={i}
								ref={(el) => {
									behind.current[i] = el
								}}
								src={other.after.video}
								poster={other.after.poster}
								muted
								loop
								playsInline
								preload="metadata"
								aria-hidden="true"
								className={`${s.ghost} ${i === 0 ? s.ghostLeft : s.ghostRight}`}
							/>
						))}

						<div className={s.phone}>
							<div className={s.screen}>
								<video
									ref={follow}
									src={slide.before.video}
									poster={slide.before.poster}
									muted
									loop
									playsInline
									preload="metadata"
									className={s.video}
								/>
								<video
									ref={lead}
									src={slide.after.video}
									poster={slide.after.poster}
									muted
									loop
									playsInline
									preload="metadata"
									onTimeUpdate={(e) => {
										const b = follow.current
										const time = e.currentTarget.currentTime
										if (
											b &&
											Math.abs(b.currentTime - time) >
												0.12
										)
											b.currentTime = time
									}}
									className={`${s.video} ${s.reveal}`}
								/>
							</div>
							{/* Each loop ends with the screen dark: the next swap's turn. */}
							<span
								aria-hidden="true"
								className={s.scan}
								onAnimationIteration={() =>
									setAt((i) => (i + 1) % SLIDES.length)
								}
							/>
							<span className={`${s.tag} ${s.tagOriginal}`}>
								{t(
									"components.scrolling_swap_promo.tag_original",
								)}
							</span>
							<span className={`${s.tag} ${s.tagCopy}`}>
								<CheckIcon />
								{t("components.scrolling_swap_promo.tag_copy")}
							</span>
						</div>

						<span aria-hidden="true" className={s.copyButton}>
							<CopyIcon />
							{t("components.scrolling_swap_promo.copy_button")}
							<span className={s.ripple} />
						</span>
						<span aria-hidden="true" className={s.cursor}>
							<CursorIcon />
						</span>
					</a>

					<div className={s.copy}>
						<div className={`${s.brand} ${s.rise}`}>
							<img
								src={wordmark}
								alt="scrolling.ai"
								className={s.wordmark}
							/>
							<span className={s.pill}>
								{t("components.scrolling_swap_promo.badge")}
							</span>
						</div>

						<h2 className={`${s.title} ${s.rise}`}>
							<Trans
								i18nKey="components.scrolling_swap_promo.title"
								components={{
									mark: <mark className={s.mark} />,
								}}
							/>
						</h2>

						<ul className={`${s.chips} ${s.rise}`}>
							{CHIPS.map(({ key, icon: ChipIcon }) => (
								<li
									key={key}
									className={`${s.chip} ${slide.kinds.includes(key) ? s.chipOn : ""}`}
								>
									<ChipIcon />
									{t(
										`components.scrolling_swap_promo.chip_${key}`,
									)}
								</li>
							))}
						</ul>

						<p className={`${s.from} ${s.rise}`}>
							{t("components.scrolling_swap_promo.from_price", {
								price: FROM_PRICE,
							})}
						</p>
					</div>

					<a
						href={swapUrl("cta")}
						target="_blank"
						rel="noopener noreferrer"
						className={`${s.cta} ${s.rise}`}
						onClick={clicked("cta")}
					>
						<span className={s.ctaLabel}>
							{t("components.scrolling_swap_promo.cta")}
						</span>
						<span className={s.ctaArrow}>
							<ArrowIcon />
						</span>
					</a>
				</div>
			</section>

			<button
				type="button"
				className={s.launcher}
				data-shown={(arrived && !open) || undefined}
				onClick={reopen}
				aria-label={t("components.scrolling_swap_promo.launcher_aria")}
				aria-hidden={!(arrived && !open) || undefined}
				tabIndex={arrived && !open ? 0 : -1}
			>
				{/* The :scrolling-scroll: emoji: the mark scrolls up and the
				    next one comes in from below, like swiping a feed. */}
				<span className={s.tile} aria-hidden="true">
					<span className={s.marks}>
						<MarkIcon />
						<MarkIcon />
					</span>
				</span>
				<span className={s.peek}>
					{t("components.scrolling_swap_promo.launcher_label")}
					<ArrowIcon />
				</span>
			</button>
		</div>
	)
}

function useReducedMotion() {
	const query = "(prefers-reduced-motion: reduce)"
	const [reduced, setReduced] = useState(
		() => !!window.matchMedia?.(query).matches,
	)
	useEffect(() => {
		const mq = window.matchMedia?.(query)
		if (!mq?.addEventListener) return
		const onChange = (e) => setReduced(e.matches)
		mq.addEventListener("change", onChange)
		return () => mq.removeEventListener("change", onChange)
	}, [])
	return reduced
}

// The scrolling.ai mark (public/brand/mark.svg on scrolling.ai).
function MarkIcon() {
	return (
		<svg viewBox="17.7 13.2 28.6 37.6" aria-hidden="true" focusable="false">
			<path
				d="M41.5 18 22.5 29.5 41.5 34.5 22.5 46"
				fill="none"
				stroke="#B8FF3B"
				strokeWidth="9.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

// Icons from Lucide, the set scrolling.ai uses.
function Svg({ size = 14, strokeWidth = 2, children }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={strokeWidth}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable="false"
		>
			{children}
		</svg>
	)
}

function UserIcon() {
	return (
		<Svg>
			<circle cx="12" cy="8" r="5" />
			<path d="M20 21a8 8 0 0 0-16 0" />
		</Svg>
	)
}

function BagIcon() {
	return (
		<Svg>
			<path d="M16 10a4 4 0 0 1-8 0" />
			<path d="M3.103 6.034h17.794" />
			<path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
		</Svg>
	)
}

function ShirtIcon() {
	return (
		<Svg>
			<path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z" />
		</Svg>
	)
}

function CopyIcon() {
	return (
		<Svg size={11} strokeWidth={2.4}>
			<rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
			<path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
		</Svg>
	)
}

function CheckIcon() {
	return (
		<Svg size={10} strokeWidth={3}>
			<path d="M20 6 9 17l-5-5" />
		</Svg>
	)
}

function XIcon() {
	return (
		<Svg size={15}>
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</Svg>
	)
}

function ArrowIcon() {
	return (
		<Svg size={16} strokeWidth={2.4}>
			<path d="M5 12h14" />
			<path d="m12 5 7 7-7 7" />
		</Svg>
	)
}

function CursorIcon() {
	return (
		<svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true">
			<path
				d="M4.04 2.85a.8.8 0 0 1 .86-.13l15.6 6.6a.8.8 0 0 1-.07 1.5l-6.3 2.03-2.03 6.3a.8.8 0 0 1-1.5.07l-6.6-15.6a.8.8 0 0 1 .04-.77z"
				fill="#fff"
				stroke="#0b0d0f"
				strokeWidth="1.4"
				strokeLinejoin="round"
			/>
		</svg>
	)
}
